import React, { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import api from "../lib/api";
import GlobalFilters from "../components/GlobalFilters";
import MyTasks from "../components/MyTasks";
import { KpiCard, PageHeader, pct, num, CHART_COLORS } from "../lib/ui";
import { useLang } from "../lib/i18n";
import {
  ResponsiveContainer, LineChart, Line, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, AreaChart, Area,
} from "recharts";
import {
  FileCheck2, Percent, Clock, Siren, Search, Microscope, Users, Building2, MapPin, UserCheck, AlertTriangle, Activity,
  ClipboardPen, AlertOctagon, ShieldAlert, Stamp,
} from "lucide-react";

const Card = ({ title, children, span }) => (
  <div className={`bg-white border border-slate-200/80 rounded-md shadow-sm p-5 ${span || ""}`}>
    <h3 className="text-sm font-semibold text-slate-800 mb-4">{title}</h3>
    {children}
  </div>
);

export default function Dashboard() {
  const [filters, setFilters] = useState({});
  const { t, lang } = useLang();
  const { data: epis } = useQuery({ queryKey: ["users"], queryFn: async () => (await api.get("/users")).data });
  const epiList = useMemo(() => (epis || []).filter((u) => u.role === "field_epi"), [epis]);

  const { data, isLoading } = useQuery({
    queryKey: ["dashboard", filters, lang],
    queryFn: async () => (await api.get("/dashboard", { params: { ...filters, lang } })).data,
  });
  const k = data?.kpis || {};

  return (
    <div data-testid="dashboard-page">
      <PageHeader title={t("Tableau de Bord Épidémiologique")} subtitle={t("Vue d'ensemble de la réponse de surveillance de terrain")} />
      <MyTasks />
      <GlobalFilters value={filters} onChange={setFilters} showEpi epis={epiList} />

      {isLoading ? (
        <div className="text-slate-400 text-sm">{t("Chargement…")}</div>
      ) : (
        <>
          <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-4">
            <KpiCard testid="kpi-reports" label="Rapports reçus" value={num(k.reports_received)} sub={`${t("sur")} ${num(k.expected_reports)} ${t("attendus")}`} icon={FileCheck2} />
            <KpiCard testid="kpi-completeness" label="Complétude" value={pct(k.completeness)} icon={Percent} tone="teal" />
            <KpiCard testid="kpi-timeliness" label="Promptitude" value={pct(k.timeliness)} icon={Clock} tone="teal" />
            <KpiCard testid="kpi-alerts" label="Alertes reçues" value={num(k.alerts_received)} icon={Siren} tone="amber" />
            <KpiCard testid="kpi-investigated" label="Alertes investiguées" value={num(k.alerts_investigated)} sub={`${t("Taux")} ${pct(k.investigation_rate)}`} icon={Search} tone="amber" />
            <KpiCard testid="kpi-suspected" label="Cas suspects" value={num(k.suspected_cases)} icon={Microscope} tone="red" />
            <KpiCard testid="kpi-confirmed" label="Cas confirmés" value={num(k.confirmed_cases)} icon={Activity} tone="red" />
            <KpiCard testid="kpi-contacts" label="Contacts suivis" value={num(k.contacts_followed)} sub={`${t("Taux")} ${pct(k.followup_rate)}`} icon={Users} />
            <KpiCard testid="kpi-facilities" label="FOSA visitées" value={num(k.facilities_visited)} icon={Building2} />
            <KpiCard testid="kpi-communities" label="Sites communaut." value={num(k.communities_visited)} icon={MapPin} tone="teal" />
            <KpiCard testid="kpi-active-epi" label="Épidémio. actifs" value={num(k.active_epidemiologists)} icon={UserCheck} />
            <KpiCard testid="kpi-high-challenges" label="Défis prioritaires" value={num(k.high_priority_challenges)} icon={AlertTriangle} tone="red" />
            <KpiCard testid="kpi-pending-rec" label="Recomm. en attente" value={num(k.recommendations_pending)} icon={AlertTriangle} tone="amber" />
            <KpiCard testid="kpi-entries-today" label="Saisies du jour" value={num(k.entries_today)} icon={ClipboardPen} tone="teal" />
            <KpiCard testid="kpi-overdue-actions" label="Actions en retard" value={num(k.overdue_actions)} icon={AlertOctagon} tone="red" />
            <KpiCard testid="kpi-dq-flags" label="Points qualité (7 j)" value={num(k.dq_open_flags)} icon={ShieldAlert} tone="amber" />
            <KpiCard testid="kpi-pending-validation" label="Rapports à valider" value={num(k.reports_pending_validation)} icon={Stamp} />
          </div>

          <div className="grid lg:grid-cols-2 gap-5 mt-5">
            <Card title={t("Courbe épidémique · cas confirmés par semaine")} span="lg:col-span-2">
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={data.epidemic_curve || []}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" />
                  <XAxis dataKey="week" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                  <Tooltip /><Legend />
                  <Bar dataKey="confirmed_cases" name={t("Cas confirmés")} fill="#F05A28" radius={[2, 2, 0, 0]} />
                  <Bar dataKey="suspected_cases" name={t("Cas suspects")} fill="#0284C7" radius={[2, 2, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </Card>

            <Card title={t("Tendance des rapports reçus")}>
              <ResponsiveContainer width="100%" height={260}>
                <AreaChart data={data.trend}>
                  <defs><linearGradient id="g1" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#F05A28" stopOpacity={0.3} /><stop offset="95%" stopColor="#F05A28" stopOpacity={0} />
                  </linearGradient></defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" />
                  <XAxis dataKey="date" tick={{ fontSize: 11 }} tickFormatter={(d) => d?.slice(5)} />
                  <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                  <Tooltip />
                  <Area type="monotone" dataKey="reports" name={t("Rapports")} stroke="#F05A28" fill="url(#g1)" strokeWidth={2} />
                </AreaChart>
              </ResponsiveContainer>
            </Card>

            <Card title={t("Alertes : reçues vs investiguées")}>
              <ResponsiveContainer width="100%" height={260}>
                <LineChart data={data.trend}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" />
                  <XAxis dataKey="date" tick={{ fontSize: 11 }} tickFormatter={(d) => d?.slice(5)} />
                  <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                  <Tooltip /><Legend />
                  <Line type="monotone" dataKey="alerts_received" name={t("Reçues")} stroke="#D97706" strokeWidth={2} dot={false} />
                  <Line type="monotone" dataKey="alerts_investigated" name={t("Investiguées")} stroke="#0284C7" strokeWidth={2} dot={false} />
                </LineChart>
              </ResponsiveContainer>
            </Card>

            <Card title={t("Activités de surveillance")}>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={data.activities} layout="vertical" margin={{ left: 20 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" horizontal={false} />
                  <XAxis type="number" tick={{ fontSize: 11 }} allowDecimals={false} />
                  <YAxis type="category" dataKey="name" tick={{ fontSize: 10 }} width={150} />
                  <Tooltip />
                  <Bar dataKey="value" name="Occurrences" fill="#F05A28" radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </Card>

            <Card title={t("Activité par Zone de Santé")}>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={data.by_zone} margin={{ bottom: 40 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" />
                  <XAxis dataKey="health_zone" tick={{ fontSize: 10 }} angle={-35} textAnchor="end" interval={0} height={60} />
                  <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                  <Tooltip /><Legend />
                  <Bar dataKey="reports" name={t("Rapports")} fill="#F05A28" radius={[2, 2, 0, 0]} />
                  <Bar dataKey="alerts_received" name={t("Alertes")} fill="#D97706" radius={[2, 2, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </Card>

            <Card title={t("Défis les plus fréquents")}>
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={data.challenges.slice(0, 8)} layout="vertical" margin={{ left: 20 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" horizontal={false} />
                  <XAxis type="number" tick={{ fontSize: 11 }} allowDecimals={false} />
                  <YAxis type="category" dataKey="name" tick={{ fontSize: 10 }} width={160} />
                  <Tooltip />
                  <Bar dataKey="value" name="Signalements" fill="#DC2626" radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </Card>

            <Card title={t("Répartition des constats clés")}>
              {data.findings.length === 0 ? <p className="text-sm text-slate-400">Aucun constat.</p> : (
                <ResponsiveContainer width="100%" height={280}>
                  <PieChart>
                    <Pie data={data.findings} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={95} label={(e) => e.value}>
                      {data.findings.map((_, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
                    </Pie>
                    <Tooltip /><Legend wrapperStyle={{ fontSize: 11 }} />
                  </PieChart>
                </ResponsiveContainer>
              )}
            </Card>
          </div>
        </>
      )}
    </div>
  );
}
